/**
 * Semantic Tokens Index
 * Aggregates all semantic token categories for the PrimeNG preset
 */

import { inputTokens, createInputTokens, createModalTokens } from '../tokens/components.js';
import { border, interactive, states, surface, text, type SemanticTokens, type AliasSet } from '../tokens/semantic.js';
import { animations } from './animations.js';
import { colorScheme } from './colorScheme.js';
import { colors } from './colors.js';
import { forms } from './forms.js';
import { icons } from './icons.js';
import { lists } from './lists.js';
import { misc } from './misc.js';
import { navigation } from './navigation.js';
import { overlays } from './overlays.js';
import { spacing } from './spacing.js';
import { typography } from './typography.js';

/**
 * Complete semantic token configuration for PrimeNG preset
 * Organized by category for better maintainability
 */
export const semanticTokens = {
  // Global settings
  disabledOpacity: misc.disabledOpacity,
  anchorGutter: misc.anchorGutter,
  
  // Colors - Text
  text: colors.text,
  
  // Colors - Surface
  surface: colors.surface,
  
  // Colors - Border (merged with spacing)
  border: {
    ...colors.border,      // color, strong, divider
    ...spacing.border,     // radius
    width: '1px',
  },
  
  // Colors - Content (merged with misc)
  content: {
    ...colors.content,     // background, color
    ...misc.content,       // border: { radius, color }
  },
  
  // Interactive states (using new nested interactive structure)
  interactive: {
    selected: {
      bg: {
        subtle: interactive.primary.subtle,
        default: interactive.primary.subtlePressed,
      },
      color: text.primary,
      border: border.primary,
    },
    secondaryBg: interactive.secondary.base,
    secondaryBgSubtle: interactive.secondary.subtle,
    secondaryBgStrong: interactive.secondary.pressed,
  },
  
  // Colors - Semantic palettes
  primary: colors.primary,
  secondary: colors.secondary,
  success: colors.success,
  warning: colors.warning,
  danger: colors.danger,
  neutral: colors.neutral,
  lock: colors.lock,
  home: colors.home,
  guest: colors.guest,
  
  // Control states (checkbox, radio, toggle â€” derived from interactive/border tokens)
  control: {
    default: border.default,
    hover: border.strong,
    checked: interactive.secondary.base,
    checkedHover: interactive.secondary.hover,
    disabled: surface.disabled,
    primary: interactive.primary.base,
    primaryHover: interactive.primary.hover,
    primaryChecked: interactive.primary.base,
    primaryCheckedHover: interactive.primary.hover,
    primaryFocus: interactive.primary.subtle,
    success: interactive.success.base,
    successHover: interactive.success.hover,
    successChecked: interactive.success.base,
    successCheckedHover: interactive.success.hover,
    successFocus: states.success.bg,
    warning: interactive.warning.base,
    warningHover: interactive.warning.hover,
    warningChecked: interactive.warning.base,
    warningCheckedHover: interactive.warning.hover,
    warningFocus: states.warning.bg,
    danger: interactive.danger.base,
    dangerHover: interactive.danger.hover,
    dangerChecked: interactive.danger.base,
    dangerCheckedHover: interactive.danger.hover,
    dangerFocus: states.danger.bg,
  },
  
  // Typography
  fontFamily: typography.fontFamily,
  fontSize: typography.fontSize,
  fontWeight: typography.fontWeight,
  lineHeight: typography.lineHeight,
  heading: typography.heading,
  body: typography.body,
  textButton: typography.textButton,
  caption: typography.caption,
  
  // Icons
  iconSize: icons.iconSize,
  iconSizes: icons.iconSizes,
  icon: icons.icon,
  
  // Animations & Transitions
  transitionDuration: animations.transitionDuration,
  transition: animations.transition,
  focusRing: animations.focusRing,
  focus: animations.focus,
  mask: animations.mask,
  
  // Layout & Spacing
  layout: spacing.layout,
  spacing: spacing.spacing,
  radius: spacing.radius,
  
  // Forms
  form: forms.form,
  formField: forms.formField,
  
  // Input-specific tokens (using inputTokens from component layer)
  input: {
    disabledBackground: inputTokens.bgDisabled,
    disabledColor: text.disabled,
  },
  
  // State tokens (success, warning, danger, info)
  states: {
    success: states.success,
    warning: states.warning,
    danger: states.danger,
    info: states.info,
  },
  
  // Lists
  list: lists.list,
  
  // Navigation
  navigation: navigation.navigation,
  
  // Overlays
  overlay: overlays.overlay,
  
  // Color Scheme
  colorScheme: colorScheme.colorScheme,
  
};

import { createColors } from './colors.js';
import { createColorScheme } from './colorScheme.js';
import { createForms } from './forms.js';
import { createAnimations } from './animations.js';
import { createLists } from './lists.js';
import { createMisc } from './misc.js';
import { createOverlays } from './overlays.js';

export function createSemanticPreset(s: SemanticTokens, al: AliasSet) {
  const it = createInputTokens(s);
  const mt = createModalTokens(s);
  const c = createColors(s, al);
  const cs = createColorScheme(s, it, mt);
  const f = createForms(s, it);
  const ov = createOverlays(s, mt);
  const an = createAnimations(s);
  const li = createLists(s);
  const mi = createMisc(s);

  return {
    disabledOpacity: mi.disabledOpacity,
    anchorGutter: mi.anchorGutter,
    text: c.text,
    surface: c.surface,
    border: {
      ...c.border,
      ...spacing.border,
      width: '1px',
    },
    content: {
      ...c.content,
      ...mi.content,
    },
    interactive: {
      selected: {
        bg: {
          subtle: s.interactive.primary.subtle,
          default: s.interactive.primary.subtlePressed,
        },
        color: s.text.primary,
        border: s.border.primary,
      },
      secondaryBg: s.interactive.secondary.base,
      secondaryBgSubtle: s.interactive.secondary.subtle,
      secondaryBgStrong: s.interactive.secondary.pressed,
    },
    primary: c.primary,
    secondary: c.secondary,
    success: c.success,
    warning: c.warning,
    danger: c.danger,
    neutral: c.neutral,
    lock: c.lock,
    home: c.home,
    guest: c.guest,
    control: {
      default: s.border.default,
      hover: s.border.strong,
      checked: s.interactive.secondary.base,
      checkedHover: s.interactive.secondary.hover,
      disabled: s.surface.disabled,
      primary: s.interactive.primary.base,
      primaryHover: s.interactive.primary.hover,
      primaryChecked: s.interactive.primary.base,
      primaryCheckedHover: s.interactive.primary.hover,
      primaryFocus: s.interactive.primary.subtle,
      success: s.interactive.success.base,
      successHover: s.interactive.success.hover,
      successChecked: s.interactive.success.base,
      successCheckedHover: s.interactive.success.hover,
      successFocus: s.states.success.bg,
      warning: s.interactive.warning.base,
      warningHover: s.interactive.warning.hover,
      warningChecked: s.interactive.warning.base,
      warningCheckedHover: s.interactive.warning.hover,
      warningFocus: s.states.warning.bg,
      danger: s.interactive.danger.base,
      dangerHover: s.interactive.danger.hover,
      dangerChecked: s.interactive.danger.base,
      dangerCheckedHover: s.interactive.danger.hover,
      dangerFocus: s.states.danger.bg,
    },
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    lineHeight: typography.lineHeight,
    heading: typography.heading,
    body: typography.body,
    textButton: typography.textButton,
    caption: typography.caption,
    iconSize: icons.iconSize,
    iconSizes: icons.iconSizes,
    icon: icons.icon,
    transitionDuration: an.transitionDuration,
    transition: an.transition,
    focusRing: an.focusRing,
    focus: an.focus,
    mask: an.mask,
    layout: spacing.layout,
    spacing: spacing.spacing,
    radius: spacing.radius,
    form: f.form,
    formField: f.formField,
    input: {
      disabledBackground: it.bgDisabled,
      disabledColor: s.text.disabled,
    },
    states: {
      success: s.states.success,
      warning: s.states.warning,
      danger: s.states.danger,
      info: s.states.info,
    },
    list: li.list,
    navigation: navigation.navigation,
    overlay: ov.overlay,
    colorScheme: cs.colorScheme,
  };
}
